import styled from "styled-components";
import { useSocket, useSessionLabels } from "../../context/SocketContext";

const PresenceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
`;

const AvatarGroup = styled.div`
  display: flex;
  align-items: center;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.$cursorColor || '#e5e7eb'};
  background-color: ${props => props.$bgColor || '#6b7280'};
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${props => props.$index > 0 ? '-6px' : '0'};
  position: relative;
  z-index: ${props => 10 - (props.$index || 0)};
  cursor: default;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.15);
    z-index: 20;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: -28px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #1f2937;
  color: white;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 100;

  ${Avatar}:hover & {
    opacity: 1;
  }
`;

const OnlineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #22c55e;
  border: 1.5px solid white;
  position: absolute;
  bottom: -1px;
  right: -1px;
`;

const UserCount = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-left: 4px;
  white-space: nowrap;
`;

const AVATAR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
];

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getColorFromName(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function PresenceBar() {
  const { connectedUsers, connected, socketId } = useSocket();
  const labels = useSessionLabels();

  if (!connected || connectedUsers.length === 0) return null;

  // 접속(소켓) 단위로 표시 — 같은 유저가 여러 브라우저로 들어오면 "이름 (1)", "이름 (2)".
  // 지금 이 탭(내 소켓)만 제외하고, 내 다른 브라우저 접속도 보여준다.
  const otherSessions = connectedUsers.filter(u => u.socket_id !== socketId);

  if (otherSessions.length === 0) return null;

  const visibleSessions = otherSessions.slice(0, 5);
  const remainingCount = otherSessions.length - visibleSessions.length;

  return (
    <PresenceContainer>
      <AvatarGroup>
        {visibleSessions.map((u, idx) => {
          const label = labels[u.socket_id] || u.user_name;
          return (
            <Avatar
              key={u.socket_id || `${u.user_id}-${idx}`}
              $index={idx}
              $cursorColor={u.cursor_color}
              $bgColor={u.cursor_color || getColorFromName(u.user_name)}
            >
              {u.avatar_url ? (
                <AvatarImage src={u.avatar_url} alt={label} />
              ) : (
                getInitials(u.user_name)
              )}
              <OnlineDot />
              <Tooltip>{label}</Tooltip>
            </Avatar>
          );
        })}
        {remainingCount > 0 && (
          <Avatar $index={visibleSessions.length} $bgColor="#9ca3af">
            +{remainingCount}
          </Avatar>
        )}
      </AvatarGroup>
      <UserCount>{otherSessions.length}명 접속 중</UserCount>
    </PresenceContainer>
  );
}
